import { useState } from 'react';
import { Button } from './ui/button';
import { Camera, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Single protagonist photo uploader.
 * Stores { photo_id, url, storage_path } and reports via onChange.
 * Uses the same /api/anecdote-photo/upload endpoint (max 5MB).
 */
export const ProtagonistPhotoUpload = ({ photo, onChange, bookId = '', context = 'protagonist' }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen supera los 5 MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('book_id', bookId);
      formData.append('context', context);
      const res = await axios.post(`${API_URL}/api/anecdote-photo/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange({
        photo_id: res.data.photo_id,
        storage_path: res.data.storage_path,
        url: `${API_URL}${res.data.photo_url}`,
      });
    } catch (err) {
      toast.error('Error al subir la foto');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const remove = () => onChange(null);

  const photoUrl = photo?.url || (photo?.photo_id ? `${API_URL}/api/anecdote-photo/${photo.photo_id}` : null);

  if (photoUrl) {
    return (
      <div className="flex items-center gap-3">
        <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-amber-200 shadow-sm flex-shrink-0 bg-slate-100">
          <img src={photoUrl} alt="Protagonista" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={remove}
            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-md"
            aria-label="Quitar foto"
            data-testid="remove-protagonist-photo"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="text-xs text-slate-500">
          <p className="font-medium text-slate-700 mb-1">Foto subida ✓</p>
          <p>Se mostrará en las invitaciones a colaboradores.</p>
        </div>
      </div>
    );
  }

  return (
    <label
      className="flex items-center gap-3 cursor-pointer p-3 border-2 border-dashed border-slate-200 rounded-lg hover:border-amber-300 hover:bg-amber-50/40 transition-colors group"
      data-testid="protagonist-photo-upload"
    >
      <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center group-hover:from-amber-200 group-hover:to-rose-200 transition-colors">
        {uploading ? <Loader2 className="w-5 h-5 text-amber-600 animate-spin" /> : <Camera className="w-5 h-5 text-amber-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-700">{uploading ? 'Subiendo...' : 'Subir foto del protagonista'}</div>
        <div className="text-xs text-slate-500">Se incluirá en las invitaciones a colaboradores · máx. 5 MB</div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="pointer-events-none"
        disabled={uploading}
      >
        {uploading ? 'Subiendo' : 'Elegir'}
      </Button>
    </label>
  );
};

export default ProtagonistPhotoUpload;
